import React, { useState } from 'react';
import StateEnum, { StateEnumTag } from '@constants/state.enum';
import UiButton from '@components/ui/button/UiButton';
import useNotification from '@hooks/notification/useNotification';
import SettingsDotsIcon from '@assets/svgs/grey-dots-icon.svg';
import DropHandIcon from '@assets/svgs/drop-hand-icon.svg';
import UiTag from '@components/ui/tag/UiTag';
import { useDrop } from 'react-dnd';
import { useTranslation } from 'next-i18next';
import { StepApplicantElement } from '@models/applicants';
import { Dropdown, Form, Input, Menu, Modal, Spin } from 'antd';
import GreyPencilIcon from '@assets/svgs/grey-pencil-icon.svg';
import { updateStep, updateStepTitle } from '@services/steps/steps-http.service';
import RejectedCard from '@components/app-components/position/position-steps/rejected-card/RejectedCard';
import ApplicantCard from '@components/app-components/position/position-steps/applicant-card/ApplicantCard';
import styles from './StepColumn.module.scss';

export type StepColumnProps = {
  stepId: string;
  positionId: string;
  stepTitle: string;
  renderTime: number;
  applicants: Array<StepApplicantElement>;
  fetchApplicants: () => void;
  rejectedStepCount: number;
  fetchRejectedCounter: () => void;
  fetchSteps: () => void;
};

const StepColumn: React.FC<StepColumnProps> = ({
  stepId,
  stepTitle,
  positionId,
  renderTime,
  applicants,
  fetchApplicants,
  rejectedStepCount,
  fetchRejectedCounter,
  fetchSteps,
}) => {
  const [form] = Form.useForm();
  const [droppableStep, setDroppableStep] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalDisable, setModalDisable] = useState<boolean>(false);
  const [creating, setCreating] = useState<boolean>(false);
  const { t: translate } = useTranslation('common');

  const [generalError] = useNotification(
    StateEnum.ERROR,
    'Something went wrong',
    'Oops, something went wrong, please try again later!',
  );

  const emptyState = () => {
    if (renderTime === 0) {
      return (
        <div className={`large-body-text ${styles.emptyColumn}`}>
          <div>Βρες το κατάλληλο προσωπικό</div>
          <div> και ξεκίνα!</div>
        </div>
      );
    }
    return <></>;
  };

  const [{ canDrop, isOver }, drop] = useDrop(
    () => ({
      accept: 'card',
      drop: async (card: { id: string; step: string }) => {
        try {
          await updateStep(positionId, droppableStep, card.id);
          await fetchApplicants();
        } catch (e: any) {
          generalError();
        }
      },
      canDrop: (card) => stepId !== card.step,
      collect: (monitor) => ({
        canDrop: monitor.canDrop(),
        isOver: monitor.isOver(),
      }),
    }),
    [droppableStep],
  );

  const renderApplicantCard = () => {
    return (
      <>
        <div className={styles.wrapStepBody} ref={drop} onDrop={() => setDroppableStep(stepId)}>
          {applicants && applicants.some((applicant) => applicant.stepId === stepId)
            ? applicants
                .filter((applicant) => applicant.stepId === stepId && !applicant.hired)
                .map((applicant: StepApplicantElement, i, arr) => (
                  <div key={applicant.id} className={arr.length - 1 === i ? styles.lastCard : ''}>
                    <ApplicantCard
                      applicant={applicant}
                      applicants={applicants.filter((obj) => obj.stepId === stepId)}
                      fetchApplicants={() => fetchApplicants()}
                      positionId={positionId}
                      step={stepTitle}
                      fetchRejectedCounter={() => fetchRejectedCounter()}
                    />
                  </div>
                ))
            : emptyState()}
        </div>
        <RejectedCard
          positionId={positionId}
          rejectedCounter={rejectedStepCount}
          fetchRejectedCounter={fetchRejectedCounter}
          fetchApplicants={fetchApplicants}
          stepTitle={stepTitle}
          stepId={stepId}
        />
      </>
    );
  };

  const hoverColumnState = () => {
    if (isOver)
      return (
        <div className={styles.isOver} ref={drop} onDrop={() => setDroppableStep(stepId)}>
          <DropHandIcon />
        </div>
      );
    return (
      <div className={styles.canDrop} ref={drop} onDrop={() => setDroppableStep(stepId)}>
        <DropHandIcon data-testid="step-column" />
      </div>
    );
  };

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const columnDotsMenu = (
    <Menu mode="vertical">
      <Menu.Item
        key="edit-column-title"
        onClick={showModal}
        className={`main-body-text ${styles.columnMenuText}`}
        icon={<GreyPencilIcon />}
      >
        {translate('rename_title')}
      </Menu.Item>
    </Menu>
  );

  const updateColumnName = async (stepTitleInput: string) => {
    try {
      setCreating(true);
      await updateStepTitle(positionId, stepId, stepTitleInput);
      await fetchSteps();
      setCreating(false);
    } catch (e: any) {
      generalError();
    }
  };

  return (
    <>
      <div className={styles.wrapStepColumn}>
        <div className={styles.wrapStepHeader}>
          <p className={`h6 ${styles.stepHeader}`}>{stepTitle}</p>
          <div className={styles.columnCounter}>
            <UiTag
              text={applicants.filter((applicant) => applicant.stepId === stepId && !applicant.hired).length.toString()}
              type={StateEnumTag.INFO}
            />
            <Dropdown dropdownRender={() => columnDotsMenu} trigger={['click']} placement="bottom">
              <UiButton
                type="ghost"
                className={styles.dotsCounter}
                icon={<SettingsDotsIcon />}
                data-testid="column-setting-dots"
              />
            </Dropdown>
          </div>
        </div>
        <div className={styles.stepColumn}>{canDrop ? hoverColumnState() : renderApplicantCard()}</div>
      </div>
      <Modal title={translate('rename_title')} open={isModalOpen} onCancel={handleCancel} footer={null}>
        <Form
          name="rename"
          form={form}
          layout="vertical"
          onFinish={() => {}}
          onFinishFailed={() => {}}
          className="antd-overwrite"
        >
          <Form.Item
            name="title"
            label={translate('step_title')}
            rules={[{ required: true, message: translate('step_title_error_message') }]}
            initialValue={stepTitle}
          >
            <Input
              name="title"
              placeholder={translate('step_title_placeholder')}
              allowClear
              maxLength={35}
              onChange={(value) => {
                if (value.target.value) {
                  setModalDisable(false);
                } else {
                  setModalDisable(true);
                }
              }}
              data-testid="rename-input"
              disabled={creating}
              prefix={creating && <Spin />}
            />
          </Form.Item>
          <div className={styles.modalButtons}>
            <UiButton
              type="primary"
              size="small"
              htmlType="submit"
              disabled={modalDisable || creating}
              onClick={() => updateColumnName(form.getFieldValue('title'))}
              className={styles.modalCreateButton}
              data-testid="rename-submit-button"
            >
              {creating ? <Spin /> : translate('rename_title')}
            </UiButton>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default StepColumn;
